import styled, { css } from 'styled-components';

import { ReactComponent as ArrowLeftIcon } from 'assets/icons/arrow-left.svg';
import { ReactComponent as ExclamationMarkIcon } from 'assets/icons/exclamation-mark.svg';

import MaintenanceNote from 'shared/components/MaintenanceNote';
import SVG from 'shared/components/SVG';
import { BoxShadowCSS } from 'shared/config/GlobalStyles';
import { ButtonIcon, TabbableButton } from 'shared/components/Button';
import { Tooltip } from 'shared/components/Tooltip';
import {
  isMaintenanceNoticeVisible,
  maintenanceNotice,
} from 'shared/config/MaintenanceNotice';
import { useBrowserStorage } from 'shared/hooks/useBrowserStorage';
import { valencia } from 'shared/config/Colors';

import { testId } from 'tests/utils';

const NOTE_WIDTH = 320;

interface CollapsibleProps {
  collapsed: boolean;
}

const MaintenanceNoticeContainer = styled.div<CollapsibleProps>`
  align-items: center;
  display: flex;
  left: 0;
  position: fixed;
  top: 50%;
  transition: transform 300ms ease;
  z-index: 11;

  transform: translate(
    ${({ collapsed }) => (collapsed ? `-${NOTE_WIDTH}px` : '0')},
    -50%
  );
`;

const MaintenanceNoteWrapper = styled.div<CollapsibleProps>`
  position: relative;

  ${({ collapsed }) =>
    collapsed &&
    css`
      pointer-events: none;
    `}
`;

const MaintenanceNoteStyled = styled(MaintenanceNote)`
  ${BoxShadowCSS};
  border-bottom-left-radius: 0;
  border-left: none;
  border-top-left-radius: 0;
  padding-right: 32px;
  width: ${NOTE_WIDTH}px;
`;

const CollapseButton = styled(ButtonIcon)`
  position: absolute;
  right: 8px;
  top: 10px;
`;

const ExpandTab = styled(TabbableButton)<CollapsibleProps>`
  ${BoxShadowCSS};
  align-items: center;
  background-color: ${({ theme }) => theme.valencia};
  border-bottom-right-radius: 8px;
  border-top-right-radius: 8px;
  display: flex;
  height: 48px;
  justify-content: center;
  position: relative;
  transition: opacity 300ms ease;
  width: 34px;

  ${({ collapsed }) =>
    !collapsed &&
    css`
      opacity: 0;
      pointer-events: none;
    `}

  svg path {
    fill: ${({ theme }) => theme.white};
  }

  &:hover ${Tooltip} {
    opacity: 1;
  }
`;

export const MaintenanceNoticeSidebar = () => {
  // note starts expanded and stays collapsed for the rest of the session
  const { value: collapsed, setItem: setCollapsed } =
    useBrowserStorage<boolean>({
      key: 'maintenanceNoticeCollapsed',
      storageType: 'sessionStorage',
    });

  if (!isMaintenanceNoticeVisible()) {
    return null;
  }

  return (
    <MaintenanceNoticeContainer
      collapsed={!!collapsed}
      {...testId('maintenance-notice-sidebar')}
    >
      <MaintenanceNoteWrapper
        collapsed={!!collapsed}
        aria-hidden={!!collapsed}
        role="note"
      >
        <MaintenanceNoteStyled />

        <CollapseButton
          type="button"
          aria-label="Collapse maintenance note"
          iconColor={valencia}
          hoverIconColor={valencia}
          tabIndex={collapsed ? -1 : 0}
          onClick={() => setCollapsed(true)}
          {...testId('maintenance-notice-collapse')}
        >
          <SVG icon={<ArrowLeftIcon />} color={valencia} />
        </CollapseButton>
      </MaintenanceNoteWrapper>

      <ExpandTab
        type="button"
        aria-label="Show maintenance note"
        collapsed={!!collapsed}
        tabIndex={collapsed ? 0 : -1}
        onClick={() => setCollapsed(false)}
        {...testId('maintenance-notice-expand')}
      >
        <ExclamationMarkIcon width={16} height={16} />

        <Tooltip position="right" bgColor={valencia}>
          {maintenanceNotice.title}
        </Tooltip>
      </ExpandTab>
    </MaintenanceNoticeContainer>
  );
};
